/* eslint-disable object-curly-newline,no-undefined,no-nested-ternary */
import { Box, useTheme } from '@mui/material'
import { getThemeColor, isStr } from 'lib/util'
import { useMemoRef, useStyles } from '@platform/react/hook'
import Image from 'ui/Element/Image'
import { useMemo } from 'react'

const styles = (theme, { shape, background, isImage, isPng }) => ({
  root: {
    height: '100%',
    width: '100%',
    ...background && { backgroundColor: getThemeColor(theme, background) },
    ...shape === 'circular' && {
      borderRadius: '50%',
      ...isPng && {
        padding: '0.313rem', // 5px
      },
      ...isImage && !isPng && {
        border: `1px solid ${getThemeColor(theme, 'gray.500')}`,
      },
    },
  },
})

/**
 * Avatar Image Presenter
 *
 * @param {Object} avatarProps
 * @param {Object} avatarProps.forwardedRef  a reference to the avatar element
 * @param {string|IconType} avatarProps.icon the icon to show as a fallback
 * @param {Object} [avatarProps.attachment]  attachment to fetch
 * @param {string} [avatarProps.value]       ref of the resource to fetch the avatar for
 * @param {string} [avatarProps.group]       attachment group
 * @param {Object} [avatarProps.api]         api information of the resource to fetch avatar
 * @returns {JSX.Element}                    the new avatar element
 */
const AvatarImage = ({ forwardedRef, icon = null, ...props }) => {
  const { attachment, group, value, api = { ref: 'avatar' }, shape } = props

  const theme = useTheme()

  const isImage = useMemo(() => attachment?.value?.mimetype?.includes('image'), [attachment])
  const isPng = useMemo(() => attachment?.value?.mimetype?.includes('png'), [attachment])

  const {
    name = 'user',
    source = name,
    color = theme.palette.white.main,
    background = theme.palette.gray[960],
    padding,
    size = '70%',
  } = icon || {}

  const classes = useStyles(styles, { shape, background, isPng, isImage })()

  const isComplexFallback = icon === null
      || icon === undefined
      || !isStr(icon)

  return (
    <Box className={classes.root}>
      <Image
        api={api}
        value={value}
        shape={shape}
        group={group}
        attachment={attachment}
        stretch={shape === 'native' || !shape}
        {...isComplexFallback
          ? { fallback: { name, source, color, background, padding, size } }
          : { fallback: icon }
        }
      />
    </Box>
  )
}

export default useMemoRef(AvatarImage, props => [props.value])
